import React, { useState, useReducer, useEffect, memo } from "react";
import { Send, MessageSquare, HeadphonesIcon } from 'lucide-react';
import { useNavigate } from "react-router-dom";
// import lib
import handleReducer, { textChange, onBlur } from '../lib/handleChange';
import isEmpty from '../lib/isEmpty';
import toastAlert from '../lib/toastAlert';
// import validation
import { contactValid } from '../validation/auth.validation';
// import services
import { createContact } from '../services/auth.service';
// import custom components
import ErrorSpan from '../custom/ErrorSpan';
import ButtonLoader from '../custom/ButtonLoader';

// imort components
const initialFormValue = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
};

const ConatctForm = () => {
    // hooks
    const navigate = useNavigate();
    // state
    const [formValue, setFormValue] = useReducer(handleReducer, initialFormValue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loader, setLoader] = useState(false);

    const { name, email, phone, subject, message } = formValue;

    // function
    const handleChange = e => textChange(e, setFormValue);
    const handleBlur = e => onBlur(e, setTouched);

    const handleSubmit = async (e) => {
        // e.preventDefault();
        setLoader(true);
        const { status, message, errors } = await createContact(formValue)
        if (status === true) {
            toastAlert('success', message, 'createContact')
            setFormValue({ type: 'RESET', value: initialFormValue })
            setTouched({})
            setErrors({});
        } else if (errors) {
            setErrors(errors)
        } else if (message) toastAlert('error', message, 'createContact')
        setLoader(false);
    };

    useEffect(() => {
        const { status, errors } = contactValid(formValue);
        if (!status) setErrors(errors);
        else setErrors({});
    }, [formValue]);

    return (
        <div className="px-6 py-20">
            <div className="max-w-7xl mx-auto">
                <div className="grid md:grid-cols-2 gap-12">
                    {/* Left Side - Form */}
                    <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/20">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                                <MessageSquare className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-white">Send us a Message</h2>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-gray-300 mb-2 font-semibold">Full Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-all"
                                    placeholder="Enter your name"
                                />
                                <ErrorSpan errors={errors} touched={touched} fieldName={'name'} />
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-300 mb-2 font-semibold">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-all"
                                        placeholder="your@email.com"
                                    />
                                    <ErrorSpan errors={errors} touched={touched} fieldName={'email'} />
                                </div>
                                <div>
                                    <label className="block text-gray-300 mb-2 font-semibold">Phone</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-all"
                                        placeholder="+91 98765 43210"
                                    />
                                    <ErrorSpan errors={errors} touched={touched} fieldName={'phone'} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-300 mb-2 font-semibold">Subject</label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={subject}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-all"
                                    placeholder="How can we help?"
                                />
                                <ErrorSpan errors={errors} touched={touched} fieldName={'subject'} />
                            </div>

                            <div>
                                <label className="block text-gray-300 mb-2 font-semibold">Message</label>
                                <textarea
                                    name="message"
                                    value={message}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    rows="5"
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-all resize-none"
                                    placeholder="Tell us more about your inquiry..."
                                ></textarea>
                                <ErrorSpan errors={errors} touched={touched} fieldName={'message'} />
                            </div>

                            {/* <button
                                type="submit"
                                className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-xl hover:shadow-cyan-500/50 transition-all flex items-center justify-center gap-2 group"
                            >
                                Send Message
                                <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button> */}
                            <ButtonLoader
                                type="button"
                                onClick={handleSubmit}
                                loader={loader}
                                disabled={!isEmpty(errors)}
                                btnName='Submit'
                            />
                        </form>
                    </div>

                    {/* Right Side - Info */}
                    <div className="space-y-8">
                        <div className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl p-8 border border-cyan-500/30">
                            <div className="flex items-center gap-3 mb-4">
                                <HeadphonesIcon className="w-8 h-8 text-cyan-400" />
                                <h3 className="text-2xl font-bold text-white">Need Help?</h3>
                            </div>
                            <p className="text-gray-300 mb-6">
                                Our support team is always ready to assist you with any questions about our binary plan, registration process, or account management.
                            </p>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-gray-300">
                                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                    <span>Quick response time</span>
                                </div>
                                <div className="flex items-center gap-3 text-gray-300">
                                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                    <span>Expert guidance</span>
                                </div>
                                <div className="flex items-center gap-3 text-gray-300">
                                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                    <span>24/7 email support</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/20">
                            <h3 className="text-2xl font-bold text-white mb-4">Frequently Asked</h3>
                            <div className="space-y-4">
                                <div>
                                    <h4 className="text-cyan-400 font-semibold mb-2">How do I join?</h4>
                                    <p className="text-gray-400 text-sm">Register with ₹2000 joining fee and start building your network immediately.</p>
                                </div>
                                <div>
                                    <h4 className="text-cyan-400 font-semibold mb-2">When do I get paid?</h4>
                                    <p className="text-gray-400 text-sm">Earnings are credited as you complete each level in the binary plan.</p>
                                </div>
                                <div>
                                    <h4 className="text-cyan-400 font-semibold mb-2">Is training provided?</h4>
                                    <p className="text-gray-400 text-sm">Yes, comprehensive training materials and support are included with your welcome kit.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default memo(ConatctForm);